import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  FileText,
  Mail,
  Calendar,
  Lightbulb,
  X,
  Send,
  Paperclip,
} from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useVoiceRecognition } from "@core/hooks/useVoiceRecognition";
import { getBrandName } from "@core/services/supportProduct";
import type {
  VoiceRecognitionState,
  VoiceRecognitionActions,
} from "@core/hooks/useVoiceRecognition";
import { VoiceInputButton } from "@core/components/shared/VoiceInputButton";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

interface SupportWidgetProps {
  position?: "bottom-right" | "bottom-left";
  primaryColor?: string;
  userId?: string;
  userEmail?: string;
}

export function SupportWidget({
  position = "bottom-right",
  primaryColor = "#374151", // Neutral dark gray — distinct from primary CTAs
}: SupportWidgetProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const brandName = getBrandName();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"chat">("chat");

  // Détection mobile/desktop pour le positionnement (réactif au resize)
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Ouverture déclenchée par un événement global (ex. depuis le sidebar mobile)
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener("support-widget:open", onOpen);
    return () => window.removeEventListener("support-widget:open", onOpen);
  }, []);

  // Nouveaux états pour le chat IA avec KB
  // Restauration depuis sessionStorage si l'utilisateur est parti puis revenu
  // (ex. il a cliqué « Voir la roadmap » et est revenu en arrière).
  const SUPPORT_CHAT_STORAGE_KEY = "doodates:support-chat-history";
  const [chatMessages, setChatMessages] = useState<
    Array<{
      id: string;
      type: "user" | "assistant" | "kb_suggestions";
      content: string;
      kbSuggestions?: Array<{
        title: string;
        content: string;
        relevance: number;
      }>;
      timestamp: Date;
    }>
  >(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(SUPPORT_CHAT_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Les dates sérialisées en JSON sont des strings → on rétablit
      return parsed.map((m: { timestamp?: string | Date }) => ({
        ...(m as object),
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      })) as never;
    } catch {
      return [];
    }
  });
  const [isSearchingKB, setIsSearchingKB] = useState(false);
  const [showTicketOption, setShowTicketOption] = useState(false);

  // Pièces jointes sélectionnées par l'utilisateur (images / PDF).
  // Elles seront uploadées via l'edge function `upload-support-attachment`
  // juste après la création du ticket. Limites alignées avec l'edge function :
  // max 3 fichiers, 5MB chacun, mimes whitelistés.
  const MAX_ATTACHMENTS = 3;
  const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
  const ALLOWED_ATTACHMENT_MIMES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/pdf",
  ];
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showRoadmapOption, setShowRoadmapOption] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState("");
  const [conversationCount, setConversationCount] = useState(0);
  // Nombre de messages enchaînés depuis qu'une bannière (ticket/roadmap)
  // est affichée. Si l'utilisateur continue la conversation sans y répondre,
  // on masque la bannière au bout de 2 messages supplémentaires pour ne pas
  // le harceler.
  const [bannerStaleCount, setBannerStaleCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reconnaissance vocale pour le support — langue réactive
  const voiceLang = (() => {
    const lng = i18n.language?.slice(0, 2) || "fr";
    if (lng === "en") return "en-US";
    if (lng === "es") return "es-ES";
    return "fr-FR";
  })();

  const voiceRecognition = useVoiceRecognition({
    lang: voiceLang,
    onTranscriptChange: (transcript) => {
      setMessage((prev) => prev + transcript);
    },
    onError: (error) => {
      console.error("Erreur reconnaissance vocale support:", error);
    },
  });

  // Capture contexte automatique
  const captureContext = () => {
    return {
      page: window.location.pathname,
      url: window.location.href,
      pollId:
        new URLSearchParams(window.location.search).get("pollId") ||
        new URLSearchParams(window.location.search).get("id"),
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      timestamp: new Date().toISOString(),
      logs: (window as any).__consoleLogs?.slice(-10) || [],
      errors: (window as any).__jsErrors?.slice(-5) || [],
    };
  };

  // Intercepter logs console
  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;

    if (!(window as any).__consoleLogs) {
      (window as any).__consoleLogs = [];
      (window as any).__jsErrors = [];

      console.log = (...args) => {
        (window as any).__consoleLogs.push({
          type: "log",
          message: args
            .map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
            .join(" "),
          timestamp: Date.now(),
        });
        originalLog.apply(console, args);
      };

      console.error = (...args) => {
        // Avoid logging our own error logs to prevent recursion
        const message = args
          .map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
          .join(" ");

        // Skip if this is a DooDates Error log (from our own error handling)
        if (message.includes("🚨 DooDates Error:")) {
          originalError.apply(console, args);
          return;
        }

        (window as any).__jsErrors.push({
          type: "error",
          message,
          stack: new Error().stack,
          timestamp: Date.now(),
        });
        originalError.apply(console, args);
      };
    }

    return () => {
      // Cleanup if needed, though usually we keep it alive
    };
  }, []);

  // Nouvelle fonction pour gérer les messages utilisateurs avec IA conversationnelle
  const handleUserMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    // Anti-spam : ne pas enchaîner des submits tant qu'une requête IA est
    // en cours (le textarea est déjà disabled via isSearchingKB, mais le
    // bouton submit peut être cliqué plus vite que le render, et Enter
    // contourne aussi cette protection).
    if (isSearchingKB || isSubmitting) return;

    // Ajouter le message utilisateur au chat
    const userMessageId = crypto.randomUUID();
    const userMessage = {
      id: userMessageId,
      type: "user" as const,
      content: message.trim(),
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setLastUserMessage(message.trim());
    setMessage(""); // Vider le champ de saisie
    setIsSearchingKB(true); // Activer l'état de recherche
    setConversationCount((prev) => prev + 1);

    try {
      // Importer le service IA
      const { SupportAIService } =
        await import("../../services/SupportAIService");
      const supportAIService = SupportAIService.getInstance();

      console.log("🤖 [WIDGET] Calling generateChatResponse with:", {
        message: message.trim(),
        historyLength: chatMessages.length,
      });

      // Générer une réponse IA conversationnelle courte
      const history = chatMessages.map((m) => ({
        role: m.type === "user" ? ("user" as const) : ("assistant" as const),
        content: m.content,
      }));
      const aiResponse = await supportAIService.generateChatResponse(
        message.trim(),
        history,
      );

      console.log("🤖 [WIDGET] AI Response received:", aiResponse);

      setIsSearchingKB(false);

      if (aiResponse) {
        // Ajouter la réponse IA au chat
        const assistantMessage = {
          id: crypto.randomUUID(),
          type: "assistant" as const,
          content: aiResponse.suggestedResponse,
          timestamp: new Date(),
        };

        setChatMessages((prev) => [...prev, assistantMessage]);

        // Détection simple d'un message d'au revoir : on masque les
        // bannières ticket/roadmap pour ne pas polluer la fin de conversation.
        // On ne déclenche que pour les messages COURTS (≤ 80 caractères)
        // pour éviter qu'un "cordialement" en fin d'un vrai bug long ne fasse
        // passer tout le message pour un au revoir.
        const farewellRegex =
          /\b(au revoir|bonne (journée|soirée|continuation)|à (bientôt|plus tard|la prochaine)|à plus|bye|ciao|bonne nuit)\b/i;
        const trimmed = message.trim();
        const isFarewell = trimmed.length <= 80 && farewellRegex.test(trimmed);
        if (isFarewell) {
          setShowTicketOption(false);
          setShowRoadmapOption(false);
        }

        // Analyse intelligente du message avec la nouvelle méthode améliorée
        try {
          if (isFarewell) {
            // Pas de classification sur un au revoir — on garde la fin douce.
            return;
          }
          console.log(
            "AI Analysis - Using new classifyAndSuggestAction method",
          );
          const classification =
            await supportAIService.classifyAndSuggestAction(lastUserMessage);

          console.log("AI Classification Result:", classification);

          if (classification) {
            const classificationType =
              classification.classification?.toLowerCase();
            const action = classification.action?.toLowerCase();
            const confidence = classification.confidence || 0;

            console.log(
              "Classification:",
              classificationType,
              "Action:",
              action,
              "Confidence:",
              confidence,
            );

            // Filets locaux : le classifieur LLM retombe parfois tout en
            // "question/conversation". On ajoute des heuristiques texte pour
            // rattraper les cas les plus nets (idée vs bug).
            const msg = message.trim();
            const bugHints =
              /\b(bug|erreur|plante|crash|cass[eé]|cassée|ne (marche|fonctionne) pas|ne s[‘’ ]enregistre|ne se (charge|lance)|impossible de|je n[‘’ ]arrive pas|page blanche|blanc[h]? d[‘’ ]ecran|probl[eè]me technique|message d[‘’ ]erreur|perd (mon|ma|mes))\b/i;
            // Heuristiques feature plus restrictives : requiert explicitement des mots
            // comme "ajouter", "intégrer", "feature request" ou "pourriez-vous ajouter"
            // pour éviter de classifier des questions normales comme features.
            const featureHints =
              /\b(j[‘’ ]aim(e|erais).*ajouter|ce serait.*bien.*ajouter|pourriez-vous ajouter|pouvez-vous ajouter|feature request|nouvelle fonctionnalit[eé]|il manque.*pouvoir|on devrait pouvoir ajouter)\b/i;
            const looksLikeBug = bugHints.test(msg);
            const looksLikeFeature = featureHints.test(msg) && !looksLikeBug;

            // Seuil de confiance augmenté à 0.75 pour ignorer les classifications
            // hésitantes du LLM (< 0.75) et éviter trop de faux positifs.
            const CONFIDENCE_THRESHOLD = 0.75;
            const trustLLM = confidence >= CONFIDENCE_THRESHOLD;
            const effectiveAction = trustLLM ? action : null;

            // On se base d'abord sur l'action proposée par le classifieur
            // (plus fiable que le label : ex. "complex" + "roadmap" doit
            // quand même ouvrir la bannière roadmap).
            // On suit aussi si cette classification a déclenché une bannière,
            // pour auto-dismiss si l'utilisateur continue sans y répondre.
            let triggeredBanner = false;
            if (effectiveAction === "ticket" || looksLikeBug) {
              console.log(
                looksLikeBug && effectiveAction !== "ticket"
                  ? "Bug heuristic - proposing ticket"
                  : "Action=ticket - proposing ticket",
              );
              setShowTicketOption(true);
              setShowRoadmapOption(false);
              triggeredBanner = true;
            } else if (effectiveAction === "roadmap" || looksLikeFeature) {
              console.log(
                looksLikeFeature && action !== "roadmap"
                  ? "Feature heuristic - proposing roadmap"
                  : "Action=roadmap - proposing roadmap",
              );
              setShowRoadmapOption(true);
              setShowTicketOption(false);
              triggeredBanner = true;
            } else if (trustLLM && classificationType === "feature") {
              // Filet de sécurité : le classifieur a bien identifié une
              // demande de fonctionnalité mais a proposé "conversation".
              // On pousse quand même vers la roadmap.
              console.log(
                "Feature classification fallback - proposing roadmap",
              );
              setShowRoadmapOption(true);
              setShowTicketOption(false);
              triggeredBanner = true;
            } else if (effectiveAction === "conversation") {
              console.log("Action=conversation - continue");
            } else if (conversationCount >= 5) {
              // Après 5+ messages sans classification claire, proposer un ticket.
              // Augmenté de 3 pour être moins agressif et laisser la conversation
              // naturelle continuer plus longtemps.
              console.log("5+ messages - proposing ticket as fallback");
              setShowTicketOption(true);
              setShowRoadmapOption(false);
              triggeredBanner = true;
            } else {
              console.log(
                "No clear action, conversation count:",
                conversationCount,
              );
            }

            // Auto-dismiss : si une bannière est déjà visible et que la
            // classification actuelle ne la re-déclenche pas, on incrémente.
            // À 2 messages enchaînés sans re-déclenchement → on la masque.
            if (triggeredBanner) {
              setBannerStaleCount(0);
            } else if (showTicketOption || showRoadmapOption) {
              const next = bannerStaleCount + 1;
              if (next >= 2) {
                console.log("Banner auto-dismissed after 2 stale messages");
                setShowTicketOption(false);
                setShowRoadmapOption(false);
                setBannerStaleCount(0);
              } else {
                setBannerStaleCount(next);
              }
            }
          } else {
            console.log("Classification returned null, using fallback logic");
            // Fallback : après 5 messages, proposer un ticket
            if (conversationCount >= 5) {
              setShowTicketOption(true);
            }
          }
        } catch (analysisError) {
          console.warn(
            "Error during AI analysis, using fallback:",
            analysisError,
          );
          // Fallback : après 5 messages, proposer un ticket
          if (conversationCount >= 5) {
            setShowTicketOption(true);
          }
        }
      } else {
        console.log("⚠️ [WIDGET] AI Response is null, using fallback");
        // Message d'erreur IA plus utile
        const errorMessage = {
          id: crypto.randomUUID(),
          type: "assistant" as const,
          content: t("errors:support_technical_bug_ticket"),
          timestamp: new Date(),
        };

        setChatMessages((prev) => [...prev, errorMessage]);
        setShowTicketOption(true);
      }
    } catch (error) {
      console.error("❌ [WIDGET] Error during AI response generation:", error);
      setIsSearchingKB(false);

      // En cas d'erreur, proposer directement un ticket
      const errorMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: t("errors:support_technical_bug_ticket"),
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, errorMessage]);
      setShowTicketOption(true);
    }
  };

  const proceedToTicketCreation = async (userMessage: string) => {
    // Validation: email obligatoire pour utilisateurs non connectés
    if (!user && !userEmail.trim()) {
      alert(t("errors:support_email_required"));
      return;
    }

    // Validation basique de l'email
    if (!user && userEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      alert(t("errors:invalid_email"));
      return;
    }

    setIsSubmitting(true);

    try {
      const context = captureContext();

      // Capturer un screenshot de la page
      let screenshot: string | null = null;
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(document.body, {
          width: window.innerWidth,
          height: window.innerHeight,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          scrollY: -window.scrollY,
          scrollX: -window.scrollX,
        });
        screenshot = canvas.toDataURL("image/png");
        console.log("📸 Screenshot captured successfully");
      } catch (screenshotError) {
        console.warn("Failed to capture screenshot:", screenshotError);
        // Continue sans screenshot si la capture échoue
      }

      // MIGRATION: Remplacer l'appel API (qui ne marche qu'avec Vercel) par un appel direct Supabase
      // Importé dynamiquement pour éviter les problèmes d'import au niveau du fichier
      const { supabase } = await import("../../lib/supabase");
      const ticketId = crypto.randomUUID();

      // Déterminer l'email à utiliser
      const emailToUse = user?.email || userEmail;

      // Analyse IA du ticket (en parallèle de la génération du titre)
      const { SupportAIService } =
        await import("../../services/SupportAIService");
      const supportAIService = SupportAIService.getInstance();

      // Analyser le screenshot si disponible
      let visualDiagnostic: string | null = null;
      if (screenshot) {
        console.log("📸 Analyzing screenshot with Vision AI...");
        visualDiagnostic = await supportAIService.analyzeScreenshot(
          screenshot,
          userMessage,
        );
        console.log("👁️ Visual diagnostic:", visualDiagnostic);
      }

      const [aiAnalysis, generatedTitle] = await Promise.all([
        supportAIService.analyzeTicket(userMessage, context),
        supportAIService.generateTicketTitle(userMessage),
      ]);

      console.log("🎫 AI Analysis:", aiAnalysis);
      console.log("🎫 Generated Title:", generatedTitle);

      // Utiliser les résultats de l'analyse IA si disponibles
      const severity = aiAnalysis?.severity.toLowerCase() || "normal";

      // Obtenir les SLA min/max pour le message
      const slaDays = SupportAIService.getSLADays(severity);
      const slaText = SupportAIService.getSLAText(severity);
      const severityToPriority: Record<string, number> = {
        low: 0,
        normal: 1,
        high: 2,
        critical: 3,
        blocker: 3,
      };

      // Mapper la classification IA vers les types de tickets valides
      const classificationToType: Record<string, string> = {
        bug: "bug",
        question: "question",
        feature: "feature",
        feedback: "feedback",
      };

      // Insérer le ticket dans Supabase.
      // NB : le schéma réel n'a PAS de colonne `message` ni `user_email` —
      // on utilise `email` et on stocke le message initial dans
      // `metadata.message` (convention lue par TicketSlidePanel côté admin,
      // ligne ~470 : `ticket.metadata?.message || ticket.subject`).
      const { data, error } = await supabase
        .from("support_tickets")
        .insert({
          id: ticketId,
          subject: generatedTitle || t("support_default_subject"),
          type: classificationToType[
            aiAnalysis?.classification?.toLowerCase() || "question"
          ],
          severity: severity,
          priority: severityToPriority[severity],
          status: "open",
          email: emailToUse,
          user_id: user?.id,
          metadata: {
            message: userMessage,
            context,
            screenshot: screenshot
              ? screenshot.substring(0, 1000) + "..."
              : null,
            visual_diagnostic: visualDiagnostic,
            ai_analysis: aiAnalysis,
          },
        })
        .select()
        .single();

      if (error) {
        console.error("❌ Error inserting ticket:", error);
        throw error;
      }

      console.log("✅ Ticket created successfully:", data);

      // Upload des pièces jointes (si présentes) maintenant que le ticket existe.
      // L'edge function utilise le service_role pour contourner RLS et stocke
      // dans `ticket.metadata.attachments` (format lu par TicketSlidePanel).
      let attachmentsUploaded = true;
      if (selectedFiles.length > 0) {
        attachmentsUploaded = await uploadAttachmentsForTicket(
          ticketId,
          selectedFiles,
        );
      }

      // Ajouter un message de confirmation dans le chat
      const attachmentNote =
        selectedFiles.length === 0
          ? ""
          : attachmentsUploaded
            ? ` ${t("support_attachments_sent", { count: selectedFiles.length })}`
            : ` ${t("errors:support_attachments_failed")}`;

      const confirmationMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: `${t("support_ticket_created", { id: ticketId.substring(0, 8), sla: slaText })}${attachmentNote}`,
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, confirmationMessage]);
      // Reset de la sélection une fois envoyée
      setSelectedFiles([]);
      setAttachmentError(null);
    } catch (error) {
      console.error("❌ Error creating ticket:", error);

      // Message d'erreur spécifique pour les utilisateurs non connectés
      const isAuthError =
        (error &&
          typeof error === "object" &&
          "message" in error &&
          (error.message as string).toLowerCase().includes("permission")) ||
        (error.message as string).toLowerCase().includes("auth") ||
        (error.message as string).toLowerCase().includes("row level security");

      const errorMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content:
          isAuthError && !user
            ? t("errors:support_ticket_login_required")
            : t("errors:support_ticket_creation_failed"),
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion des pièces jointes
  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setAttachmentError(null);
    const incoming = Array.from(fileList);

    // Refuser les fichiers non supportés / trop gros AVANT de les ajouter
    for (const f of incoming) {
      if (!ALLOWED_ATTACHMENT_MIMES.includes(f.type)) {
        setAttachmentError(
          t("errors:support_attachment_bad_format", { name: f.name }),
        );
        return;
      }
      if (f.size > MAX_ATTACHMENT_SIZE) {
        setAttachmentError(
          t("errors:support_attachment_too_large", {
            name: f.name,
            size: (f.size / 1024 / 1024).toFixed(1),
          }),
        );
        return;
      }
    }

    setSelectedFiles((prev) => {
      const combined = [...prev, ...incoming];
      if (combined.length > MAX_ATTACHMENTS) {
        setAttachmentError(
          t("errors:support_attachment_too_many", { max: MAX_ATTACHMENTS }),
        );
        return combined.slice(0, MAX_ATTACHMENTS);
      }
      return combined;
    });

    // Reset l'input pour permettre de re-sélectionner le même fichier après suppression
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  // Upload des pièces jointes après création du ticket.
  // On tolère un échec ici : le ticket est déjà créé, on informe juste
  // l'utilisateur dans le chat si les pièces jointes n'ont pas pu remonter.
  const uploadAttachmentsForTicket = async (
    ticketId: string,
    files: File[],
  ): Promise<boolean> => {
    if (files.length === 0) return true;
    try {
      const { supabase } = await import("../../lib/supabase");
      const formData = new FormData();
      formData.append("ticketId", ticketId);
      for (const f of files) formData.append("files", f, f.name);

      const { data, error } = await supabase.functions.invoke(
        "upload-support-attachment",
        { body: formData },
      );
      if (error) {
        console.error("❌ Attachment upload failed:", error);
        return false;
      }
      console.log("📎 Attachments uploaded:", data);
      return true;
    } catch (err) {
      console.error("❌ Attachment upload threw:", err);
      return false;
    }
  };

  // Fonctions utilitaires pour le widget
  const resetChat = () => {
    setChatMessages([]);
    setMessage("");
    setShowTicketOption(false);
    setShowRoadmapOption(false);
    setLastUserMessage("");
    setConversationCount(0);
    setSelectedFiles([]);
    setAttachmentError(null);
  };

  const startNewConversation = () => {
    if (chatMessages.length > 0) {
      // Ajouter un message de confirmation de nouvelle conversation
      const resetMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: t("support_new_conversation"),
        timestamp: new Date(),
      };
      setChatMessages([resetMessage]);
    }
    resetChat();
  };

  const handleTicketOption = (createTicket: boolean) => {
    if (createTicket) {
      proceedToTicketCreation(lastUserMessage);
    } else {
      setShowTicketOption(false);
    }
  };

  const handleRoadmapOption = (goToRoadmap: boolean) => {
    if (goToRoadmap) {
      // Rediriger vers la roadmap en pré-remplissant le formulaire
      // « Proposer une fonctionnalité » avec le dernier message utilisateur.
      // La page roadmap lit ce param pour ouvrir le modal et remplir le champ.
      const suggestion = encodeURIComponent(lastUserMessage || "");
      window.location.href = suggestion
        ? `/roadmap?suggest=${suggestion}`
        : "/roadmap";
    } else {
      setShowRoadmapOption(false);
    }
  };

  // Effet pour scroller vers le bas du chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Persister la conversation pour survivre à une navigation + retour
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (chatMessages.length === 0) {
        sessionStorage.removeItem(SUPPORT_CHAT_STORAGE_KEY);
      } else {
        sessionStorage.setItem(
          SUPPORT_CHAT_STORAGE_KEY,
          JSON.stringify(chatMessages),
        );
      }
    } catch {
      // quota exceeded ou mode privé — pas bloquant
    }
  }, [chatMessages]);

  // Raccourci clavier Shift+?
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === "?") {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // On /create, the page has its own chat input + send button at the bottom.
  // We don't hide the widget — we lift it above that input area so it doesn't
  // cover the send button. ~96px clears a 2-line textarea + buttons + margin.
  const isOnCreatePage = pathname.startsWith("/create");
  const chatInputClearance = isOnCreatePage && isMobile ? 96 : 0;
  const widgetBottom = `calc(${isMobile ? "1rem" : "1.5rem"} + env(safe-area-inset-bottom) + var(--bottom-nav-height, 0px) + ${chatInputClearance}px)`;

  return (
    <div className="z-[70] font-sans" style={{ zIndex: 9999 }}>
      {/* Bouton flottant — masqué sur mobile (point d'entrée via le sidebar) */}
      {!isOpen && !isMobile && (
        <button
          onClick={() => {
            console.log("🎯 [WIDGET] Click event received - opening widget", {
              isOpen,
              position,
            });
            setIsOpen(true);
          }}
          className={`fixed ${position === "bottom-right" ? "right-6" : "left-6"}
            w-14 h-14 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center justify-center
            transition-all duration-300 hover:scale-110 hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)] focus:outline-none focus:ring-4 focus:ring-gray-300`}
          style={{
            backgroundColor: primaryColor,
            // Sit above iOS Safari home indicator, the bottom navigation tabs
            // (--bottom-nav-height set by MobileNavigationTabs when mounted),
            // AND the on-page chat input on /create.
            bottom: widgetBottom,
          }}
          aria-label={t("support_open")}
        >
          {/* Mobile: 20px (Apple HIG-aligned 44px button); Desktop: 24px (industry-standard 48px button). */}
          <Bot className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} text-white`} />
        </button>
      )}

      {/* Widget ouvert */}
      {isOpen && (
        <div
          className={`fixed ${
            isMobile
              ? "inset-x-2 bottom-2 w-auto max-h-[85vh]"
              : `${position === "bottom-right" ? "right-6" : "left-6"} w-[400px] h-[600px] max-h-[85dvh] max-w-[calc(100vw-32px)]`
          } bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ease-out border border-gray-700`}
          style={isMobile ? undefined : { bottom: widgetBottom }}
        >
          {/* Header */}
          <div
            className="p-5 flex items-center justify-between relative overflow-hidden"
            style={{ backgroundColor: primaryColor }}
          >
            {/* Pattern de fond subtil */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "16px 16px",
              }}
            ></div>

            <div className="text-white relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg tracking-tight leading-tight">
                  {t("support_assistant_title", { brand: brandName })}
                </h3>
                <p className="text-sm text-blue-100">
                  {t("support_assistant_subtitle")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Bouton Nouvelle conversation - seulement si des messages existent */}
              {chatMessages.length > 1 && (
                <button
                  onClick={startNewConversation}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors relative z-10"
                  aria-label={t("support_new_conversation_label")}
                  title={t("support_new_conversation_label")}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors relative z-10"
                aria-label={t("close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body - Uniquement le chat */}
          <div className="flex-1 overflow-y-auto bg-gray-800 flex flex-col">
            <div className="flex-1 flex flex-col p-4 w-full h-full">
              {/* Messages du chat */}
              <div className="flex-1 space-y-4 mb-4 overflow-y-auto">
                {/* Message de bienvenue de l'IA */}
                {chatMessages.length === 0 && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="bg-gray-700 border border-gray-600 shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-gray-200">
                      <p>
                        {t("support_welcome_greeting", { brand: brandName })}
                      </p>
                      <p className="mt-2 text-xs text-gray-400">
                        {t("support_welcome_capabilities")}
                      </p>
                      <p className="mt-3 text-xs text-blue-300">
                        {t("support_welcome_prompt")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Messages du chat */}
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 max-w-[85%] ${msg.type === "user" ? "ml-auto" : ""}`}
                  >
                    {msg.type === "user" ? (
                      <div className="w-8 h-8 rounded-full bg-gray-600 flex-shrink-0 flex items-center justify-center">
                        <span className="text-xs text-gray-300 font-semibold">
                          {t("support_you")}
                        </span>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-blue-400" />
                      </div>
                    )}
                    <div
                      className={`bg-gray-700 border border-gray-600 shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-gray-200 ${msg.type === "user" ? "bg-blue-600 border-blue-500" : ""}`}
                    >
                      <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-gray-200 prose-strong:text-white prose-a:text-blue-400">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSanitize]}
                          components={{
                            a: ({ href, children, ...props }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 underline transition-colors"
                                {...props}
                              >
                                {children}
                              </a>
                            ),
                            strong: ({ children, ...props }) => (
                              <strong
                                className="text-white font-semibold"
                                {...props}
                              >
                                {children}
                              </strong>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>

                      {/* Suggestions KB (ancien système - à supprimer complètement) */}
                      {msg.type === "kb_suggestions" && msg.kbSuggestions && (
                        <div className="mt-3 space-y-2">
                          {msg.kbSuggestions.map((article, index) => (
                            <div
                              key={index}
                              className="p-3 bg-gray-800 rounded-lg border border-gray-600 hover:border-blue-500 transition-colors"
                            >
                              <h4 className="font-medium text-blue-400 text-sm mb-2">
                                {article.title}
                              </h4>
                              <div className="text-xs text-gray-300 whitespace-pre-wrap line-clamp-4">
                                {article.content}
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                {t("support_relevance")}{" "}
                                {Math.round(article.relevance * 100)}%
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Message de recherche KB */}
                {isSearchingKB && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="bg-gray-700 border border-gray-600 shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                        <p>{t("support_searching")}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Question de création de ticket */}
                {showTicketOption && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="bg-gray-700 border border-gray-600 shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-gray-200">
                      <p className="mb-3">{t("support_ticket_prompt")}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleTicketOption(true)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                        >
                          {t("support_ticket_yes")}
                        </button>
                        <button
                          onClick={() => handleTicketOption(false)}
                          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                        >
                          {t("support_ticket_no")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Question de proposition de roadmap */}
                {showRoadmapOption && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex-shrink-0 flex items-center justify-center">
                      <Lightbulb className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="bg-gray-700 border border-gray-600 shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-gray-200">
                      <p className="mb-3">{t("support_roadmap_prompt")}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRoadmapOption(true)}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors"
                        >
                          {t("support_roadmap_yes")}
                        </button>
                        <button
                          onClick={() => handleRoadmapOption(false)}
                          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                        >
                          {t("support_roadmap_no")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Formulaire au bas du chat */}
              <form
                onSubmit={handleUserMessage}
                className="bg-gray-700 p-3 rounded-2xl border border-gray-600 shadow-sm relative z-50 pointer-events-auto focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow"
              >
                {/* Email uniquement si on va créer un ticket */}
                {!user && showTicketOption && (
                  <div className="px-2 pb-2 border-b border-gray-600 mb-2">
                    <input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder={t("support_email_placeholder")}
                      className="w-full px-3 py-2 text-sm bg-gray-600 border border-gray-500 rounded-lg text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isSubmitting || isSearchingKB}
                    />
                  </div>
                )}
                {/* Aperçu des pièces jointes sélectionnées */}
                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-1 pb-2 border-b border-gray-600 mb-2">
                    {selectedFiles.map((file, index) => {
                      const isImage = file.type.startsWith("image/");
                      const previewUrl = isImage
                        ? URL.createObjectURL(file)
                        : null;
                      return (
                        <div
                          key={`${file.name}-${index}`}
                          className="relative group flex items-center gap-2 bg-gray-600 rounded-lg p-1 pr-2 border border-gray-500"
                          title={`${file.name} — ${(file.size / 1024).toFixed(0)} Ko`}
                        >
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={file.name}
                              className="w-10 h-10 object-cover rounded"
                              onLoad={() => URL.revokeObjectURL(previewUrl)}
                            />
                          ) : (
                            <div className="w-10 h-10 flex items-center justify-center bg-gray-700 rounded">
                              <FileText className="w-5 h-5 text-gray-300" />
                            </div>
                          )}
                          <span className="text-xs text-gray-200 max-w-[120px] truncate">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            disabled={isSubmitting}
                            className="ml-1 p-0.5 rounded hover:bg-gray-500 text-gray-300 hover:text-white transition-colors"
                            aria-label={t("support_remove_file", {
                              name: file.name,
                            })}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {attachmentError && (
                  <div className="px-2 pb-2 text-xs text-red-400">
                    {attachmentError}
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("support_message_placeholder")}
                    className="flex-1 px-3 py-2 max-h-[80px] min-h-[40px] text-sm resize-none focus:outline-none bg-transparent text-gray-100 placeholder:text-gray-400"
                    disabled={
                      isSubmitting ||
                      isSearchingKB ||
                      voiceRecognition.isListening
                    }
                    rows={Math.min(3, Math.max(1, message.split("\n").length))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (message.trim() && !isSubmitting && !isSearchingKB)
                          handleUserMessage(e as any);
                      }
                    }}
                  />

                  {/* Bouton pièce jointe */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => handleFilesSelected(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={
                      isSubmitting ||
                      isSearchingKB ||
                      selectedFiles.length >= MAX_ATTACHMENTS
                    }
                    className="p-2 rounded-xl text-gray-300 hover:text-white hover:bg-gray-600 disabled:opacity-50 disabled:hover:bg-transparent transition-colors flex items-center justify-center shrink-0"
                    aria-label={t("support_attach_file")}
                    title={
                      selectedFiles.length >= MAX_ATTACHMENTS
                        ? t("support_attach_max", { max: MAX_ATTACHMENTS })
                        : t("support_attach_hint")
                    }
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  {/* Bouton reconnaissance vocale */}
                  <VoiceInputButton
                    voiceRecognition={voiceRecognition}
                    size="icon"
                    className="shrink-0"
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting || isSearchingKB || !message.trim()}
                    className="p-2 rounded-xl text-white disabled:opacity-50 disabled:bg-gray-300 bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center shrink-0"
                  >
                    {isSubmitting || isSearchingKB ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </form>

              <div className="hidden md:block text-center mt-3 mb-1">
                <p className="text-[10px] text-gray-500 font-medium">
                  {t("support_keyboard_hint")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
